import React from 'react';
import Parcel from 'single-spa-react/parcel';
import { catchSyncStacktrace } from 'auto-trace';
import classNames from 'classnames';
import Cancelable from 'react-disposable-decorator';
import { CprButton, CpTooltip } from 'canopy-styleguide!sofe';
import { TopnavSecondary } from 'primary-navbar!sofe';
import toasts from 'toast-service!sofe';
import { featureEnabled } from 'feature-toggles!sofe';
import { userHasOnlyLicense } from 'cp-client-auth!sofe';

import { archiveContact, deleteContact } from './contacts.resource.js';
import styles from './contact-menu.styles.css';
import Participants from './participants.component.js';
import { ContactNameDropdown } from './dropdown.component.js';
import Tabs from './tabs.component.js';
import { archiveContactFromLocalStorage } from './recent-contacts.helper.js';

class ContactMenu extends React.Component {
  state = {
    form: {
      business_name: this.props.contact.business_name,
      first_name: this.props.contact.first_name,
      last_name: this.props.contact.last_name,
      middle_name: this.props.contact.middle_name,
    },
    contactType: this.props.contact.contact_type,
    isEditing: false,
    showContactEditModal: false,
    showDeleteContactsModal: false,
  };

  componentWillReceiveProps(props) {
    this.resetState(props);
  }

  resetState = props => {
    this.setState({
      form: {
        business_name: props.contact.business_name,
        first_name: props.contact.first_name,
        last_name: props.contact.last_name,
      },
      contactType: props.contact.contact_type,
      isEditing: false,
      showDeleteContactsModal: false,
    });
  };

  handleFormChange = (property, event) => {
    this.setState({
      form: {
        ...this.state.form,
        [property]: event.target.value,
      },
    });
  };

  showEditModal = () => {
    this.setState({
      showContactEditModal: true,
    });
  };

  hideEditModal = updated => {
    if (updated) {
      window.dispatchEvent(new CustomEvent('contacts-ui::contact-updated'));
    }
    this.setState({
      showContactEditModal: false,
    });
  };

  showDeleteModal = () => {
    this.setState({
      showDeleteContactsModal: true,
    });
  };

  hideDeleteModal = () => {
    this.setState({
      showDeleteContactsModal: false,
    });
  };

  setInputRef = ref => {
    this.input = ref;
  };

  startEditing = event => {
    this.setState(
      {
        isEditing: true,
      },
      () => {
        this.input.focus();
        this.input.select();
      }
    );
  };

  stopEditing = event => {
    this.setState({
      isEditing: false,
    });
  };

  cancel = event => {
    this.resetState(this.props);
    this.stopEditing();
  };

  archiveContact = event => {
    archiveContact(this.props.contact.id)
      .then(() => {
        toasts.successToast(`Successfully archived ${this.props.contact.name}`);

        archiveContactFromLocalStorage(this.props.contact.id, window.loggedInUser.id);
        window.location = '/#/clients';
      })
      .catch(ex => {
        toasts.warningToast("Sorry, we couldn't archive the contact. Please try again later.");
      });
  };

  deleteContact = event => {
    deleteContact(this.props.contact.id)
      .then(() => {
        archiveContactFromLocalStorage(this.props.contact.id, window.loggedInUser.id);
        window.location = '/#/clients';
      })
      .catch(ex => {
        toasts.warningToast("Sorry, we couldn't delete the contact. Please try again later.");
      });
  };

  hasAccess = permission => {
    return (
      this.props.context.loggedInUser.permissions &&
      this.props.context.loggedInUser.permissions[permission]
    );
  };

  render() {
    const { contact, context } = this.props
    const { loggedInUser } = context;
    const { is_business, business_name, first_name, middle_name, last_name, display_name, initials, id, users } = contact;
    const name = display_name ?
      display_name : is_business ? // business_name could be set for non-businesses. We want to show first/middle/last for these.
      business_name : [first_name, middle_name, last_name].filter(i => i).join(' ')
    const transcriptsOnly = featureEnabled('toggle_transcripts_module') && userHasOnlyLicense(loggedInUser, 'transcripts');

    const staticName = (
      <div
        className={`${styles.name} ${
          context.loggedInUser.role === 'TeamMember'
            ? transcriptsOnly ? 'cp-mt-24' : 'cp-mt-12'
            : 'cp-mt-16'
        }`}>
        <CpTooltip
          text={name}
          disabled={name.length < 25}
          delay={200}
          position={'bottom'}>
          <div style={{ maxWidth: 'calc(100% - 7rem)'}} className="cps-title cps-pull-left cps-ellipsis ">
            {name}
          </div>
        </CpTooltip>
        {this.hasAccess('contacts_archive') || this.hasAccess('contacts_create_edit') ? (
          <ContactNameDropdown
            showContactEditModal={this.showEditModal}
            archiveContact={this.archiveContact}
            showDeleteModal={this.showDeleteModal}
            context={context}
          />
        ) : null}
      </div>
    );

    const contactTypeValue = this.state.contactType;
    const contactTypeClasses = classNames(
      styles.contactType,
      'cps-pull-left',
      { 'cps-color-primary': contactTypeValue == 'client' },
      { 'cps-info': contactTypeValue == 'prospect' },
      { 'cps-gray-10': contactTypeValue == 'other' }
    );

    const capitalizedContactType = contactTypeValue.charAt(0).toUpperCase() + contactTypeValue.slice(1);

    const contactTypeIconClasses = classNames(
      'cps-icon',
      { 'cps-icon-client': !is_business },
      { 'cps-icon-business': is_business }
    );

    const contactType = (
      <div className={contactTypeClasses}>
        <span className={contactTypeIconClasses} style={{ color: 'inherit' }} />
        <span className="cps-caption cps-margin-left-4">{capitalizedContactType}</span>
      </div>
    );

    return (
      <TopnavSecondary className={styles.contactMenu}>
        <div className="cps-topnav-secondary__content +tall">
          <div className="cps-row">
            <div className="cps-col-xs-8">
              <div>
                <div className="cps-label-square +large cps-pull-left cps-margin-top-16 cps-margin-right-24">
                  <div className="cps-center-vertical">{initials}</div>
                </div>

                <span className="cps-color-primary-text cps-wt-semibold">{staticName}</span>
                {!transcriptsOnly && loggedInUser.role === 'TeamMember' && contactType}
              </div>
            </div>
            <div className="cps-col-xs-4">
              {!transcriptsOnly && this.hasAccess('team_member') && (
                <Participants
                  hasAccess={this.hasAccess}
                  contactId={id}
                  users={users}
                  loggedInUser={loggedInUser}
                />
              )}
            </div>
          </div>
        </div>
        <Tabs contact={contact} hasAccess={this.hasAccess} userRole={loggedInUser.role} />
        {this.state.showContactEditModal && (
          <Parcel
            config={() =>
              SystemJS.import('contacts-ui!sofe').then(contactsUI => {
                return contactsUI.CreateEditContact;
              })
            }
            appendTo={document.body}
            handleError={catchSyncStacktrace}
            onModalHide={this.hideEditModal}
            contact={contact}
            isCreate={false}
          />
        )}
        {this.state.showDeleteContactsModal ? (
          <div className="cps-modal">
            <div className="cps-modal__screen" />
            <div className="cps-modal__dialog cps-card__height-3">
              <div className="cps-card__header cps-subheader-sm">
                <span>Delete Contact</span>
                <a className="cps-modal__dialog__close cps-icon cps-icon-close" onClick={this.hideDeleteModal} />
              </div>
              <div className="cps-card__banner +large +bg-warning">
                <i className="cps-icon cps-icon-error cps-warning">
                  <span>Welcome to the Danger Zone! Please read this.</span>
                </i>
              </div>
              <div className="cps-card__body cp-clients-index__modal-body">
                By deleting this contact, you will be permanently deleting all billing items, contact data, dates,
                engagements, files, notes, and tasks related to the contact. You will not be able to restore this data.
                Are you sure you want to delete this contact?
              </div>
              <hr className="cps-margin-0" />
              <div className="cps-card__body">
                <CprButton actionType="primary" onClick={this.deleteContact}>
                  Delete contact
                </CprButton>
                <CprButton actionType="flat" className="cps-light-gray" onClick={this.hideDeleteModal}>
                  Cancel
                </CprButton>
              </div>
            </div>
          </div>
        ) : null}
      </TopnavSecondary>
    );
  }
}

export default Cancelable(ContactMenu);
